import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { DataTable, FilterBar, ConfirmDialog } from '../shared';
import {
  listTenants,
  createTenant,
  updateTenant,
  updateTenantStatus,
  getTenantStats,
  generateSubdomain,
} from '../../services/tenantManagementService';
import { generatePassword } from '../../services/userManagementService';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../theme/tokens';
import { getClinicStatus, getStatusColor } from '../../utils/statusUtils';
import toast from 'react-hot-toast';

/**
 * ClinicManagement Component
 * 
 * Comprehensive clinic/tenant management interface for super admins.
 * Features: list, search, filter, view, edit, create clinics with admin users.
 * All operations persist to Supabase database.
 */
const ClinicManagement = () => {
  const { profile } = useAuth();
  
  // Table state
  const [clinics, setClinics] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [clinicStats, setClinicStats] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    location_city: '',
    phone: '',
    email: '',
  });

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    location_city: '',
    phone: '',
    email: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    adminPassword: '',
  });

  // Fetch clinics
  const fetchClinics = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await listTenants({
        q: searchTerm,
        status: statusFilter,
        page,
        pageSize,
        sortBy,
        sortOrder,
      });

      if (result.error) {
        throw result.error;
      }

      setClinics(result.data.rows);
      setTotalCount(result.data.total);
    } catch (err) {
      console.error('Error fetching clinics:', err);
      setError('Failed to load clinics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, page, pageSize, sortBy, sortOrder]);

  // Load clinics on mount and when dependencies change
  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Clinic Name',
      sortable: true,
      render: (row) => row.name || 'N/A',
    },
    {
      key: 'location_city',
      header: 'Location',
      sortable: true,
      width: 150,
      render: (row) => row.location_city || 'Not specified',
    },
    {
      key: 'phone',
      header: 'Phone',
      width: 150,
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: 120,
      render: (row) => {
        const displayStatus = getClinicStatus(row.status);
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
          />
        );
      },
    },
    {
      key: 'created_at',
      header: 'Onboarded',
      sortable: true,
      width: 150,
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
    },
  ];

  // Action handlers
  const handleView = async (clinic) => {
    setSelectedClinic(clinic);
    // Load clinic stats
    const statsResult = await getTenantStats(clinic.id);
    if (!statsResult.error) {
      setClinicStats(statsResult.data);
    }
    setViewDialogOpen(true);
  };

  const handleEdit = (clinic) => {
    setSelectedClinic(clinic);
    setEditForm({
      name: clinic.name || '',
      location_city: clinic.location_city || '',
      phone: clinic.phone || '',
      email: clinic.email || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeactivate = (clinic) => {
    setSelectedClinic(clinic);
    setConfirmAction('deactivate');
    setConfirmDialogOpen(true);
  };

  const handleActivate = (clinic) => {
    setSelectedClinic(clinic);
    setConfirmAction('activate');
    setConfirmDialogOpen(true);
  };

  const handleCreate = () => {
    const password = generatePassword();
    setCreateForm({
      name: '',
      location_city: '',
      phone: '',
      email: '',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      adminPhone: '',
      adminPassword: password,
    });
    setCreateDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedClinic) return;

    const newStatus = confirmAction === 'activate' ? 'active' : 'inactive';
    
    const result = await updateTenantStatus(
      selectedClinic.id,
      newStatus
    );

    if (result.error) {
      toast.error(`Failed to ${confirmAction} clinic`);
    } else {
      await fetchClinics();
    }

    setConfirmDialogOpen(false);
    setSelectedClinic(null);
    setConfirmAction(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedClinic) return;

    setLoading(true);
    try {
      const result = await updateTenant(
        selectedClinic.id,
        editForm
      );

      if (result.error) {
        throw result.error;
      }

      await fetchClinics();
      setEditDialogOpen(false);
      setSelectedClinic(null);
    } catch (err) {
      console.error('Error updating clinic:', err);
      toast.error('Failed to update clinic');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClinic = async () => {
    setLoading(true);
    try {
      const result = await createTenant({
        name: createForm.name,
        location_city: createForm.location_city,
        phone: createForm.phone,
        email: createForm.email,
        adminData: {
          first_name: createForm.adminFirstName,
          last_name: createForm.adminLastName,
          email: createForm.adminEmail,
          phone: createForm.adminPhone,
          password: createForm.adminPassword,
        },
        onboarded_by: profile?.id,
      });

      if (result.error) {
        throw result.error;
      }

      await fetchClinics();
      setCreateDialogOpen(false);
      toast.success('Clinic created! Make sure to save the admin credentials.');
    } catch (err) {
      console.error('Error creating clinic:', err);
      toast.error('Failed to create clinic');
    } finally {
      setLoading(false);
    }
  };

  const regeneratePassword = () => {
    setCreateForm({ ...createForm, adminPassword: generatePassword() });
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(createForm.adminPassword);
    toast.success('Password copied to clipboard!');
  };

  const copyCredentials = () => {
    const credentials = `Clinic: ${createForm.name}
Admin Name: ${createForm.adminFirstName} ${createForm.adminLastName}
Admin Email: ${createForm.adminEmail}
Admin Password: ${createForm.adminPassword}
Login URL: https://alpha01.com/login`;
    
    navigator.clipboard.writeText(credentials);
    toast.success('All credentials copied to clipboard!');
  };

  // Actions slot for table
  const actionsSlot = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <IconButton
        size="small"
        onClick={() => handleView(row)}
        aria-label={`View ${row.name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleEdit(row)}
        aria-label={`Edit ${row.name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      {row.status === 'active' ? (
        <IconButton
          size="small"
          onClick={() => handleDeactivate(row)}
          aria-label={`Deactivate ${row.name}`}
          color="error"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.danger}`,
              outlineOffset: 2,
            },
          }}
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={() => handleActivate(row)}
          aria-label={`Activate ${row.name}`}
          color="success"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.success}`,
              outlineOffset: 2,
            },
          }}
        >
          <CheckCircleIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Clear filters handler
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPage(0);
  };

  return (
    <Box sx={{ py: 0, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.125rem' }}>
            Clinic Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Onboard and manage clinics in the system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddBusinessIcon />}
          onClick={handleCreate}
          size="small"
        >
          Onboard New Clinic
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={handleClearFilters}
        placeholder="Search by clinic name or city..."
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      {/* Stats */}
      <Box sx={{ mb: 1, mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {totalCount} total clinic{totalCount !== 1 ? 's' : ''}
          {statusFilter !== 'all' && ` (filtered by ${statusFilter})`}
        </Typography>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={clinics}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortDir={sortOrder}
        loading={loading}
        error={error}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortOrder(dir);
        }}
        getRowId={(row) => row.id}
        actionsSlot={actionsSlot}
        emptyMessage="No clinics found. Click 'Onboard New Clinic' to get started."
        ariaLabel="clinics table"
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setClinicStats(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clinic Details</DialogTitle>
        <DialogContent>
          {selectedClinic && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Clinic Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedClinic.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Location</Typography>
                  <Typography variant="body1">{selectedClinic.location_city || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedClinic.status} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedClinic.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedClinic.email || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Subdomain</Typography>
                  <Typography variant="body1">{selectedClinic.subdomain}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Onboarded</Typography>
                  <Typography variant="body1">
                    {selectedClinic.created_at ? new Date(selectedClinic.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
                {clinicStats && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.875rem' }}>Clinic Statistics</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">Total Users: {clinicStats.totalUsers}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Patients: {clinicStats.patients}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Staff: {clinicStats.staff}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">Admins: {clinicStats.admins}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewDialogOpen(false);
            setClinicStats(null);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Clinic</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Clinic Name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="City/Location"
                  value={editForm.location_city}
                  onChange={(e) => setEditForm({ ...editForm, location_city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Onboard New Clinic</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: tokens.space.x2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              This will create the clinic AND a clinic admin user. Save the admin credentials!
            </Alert>
            
            <Typography variant="h6" sx={{ mb: 2 }}>Clinic Information</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Clinic Name"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  helperText={`Subdomain: ${generateSubdomain(createForm.name || 'clinic-name')}`}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City/Location"
                  value={createForm.location_city}
                  onChange={(e) => setCreateForm({ ...createForm, location_city: e.target.value })}
                  placeholder="Lusaka, Zambia"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Clinic Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 2 }}>Clinic Admin Setup</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="Admin First Name"
                  value={createForm.adminFirstName}
                  onChange={(e) => setCreateForm({ ...createForm, adminFirstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="Admin Last Name"
                  value={createForm.adminLastName}
                  onChange={(e) => setCreateForm({ ...createForm, adminLastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Admin Email"
                  type="email"
                  value={createForm.adminEmail}
                  onChange={(e) => setCreateForm({ ...createForm, adminEmail: e.target.value })}
                  helperText="This will be the admin's login username"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Admin Phone"
                  value={createForm.adminPhone}
                  onChange={(e) => setCreateForm({ ...createForm, adminPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Admin Password"
                  value={createForm.adminPassword}
                  onChange={(e) => setCreateForm({ ...createForm, adminPassword: e.target.value })}
                  helperText="Auto-generated. Admin can change on first login."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={copyPassword} edge="end" title="Copy password">
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton onClick={regeneratePassword} edge="end" title="Generate new password">
                          <RefreshIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={copyCredentials}
          >
            Copy All Credentials
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateClinic} 
            variant="contained" 
            disabled={loading || !createForm.name || !createForm.adminFirstName || !createForm.adminLastName || !createForm.adminEmail}
          >
            {loading ? 'Creating...' : 'Create Clinic'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction === 'activate' ? 'Activate Clinic' : 'Deactivate Clinic'}
        message={
          selectedClinic
            ? `Are you sure you want to ${confirmAction} ${selectedClinic.name}? This will affect all users in this clinic.`
            : ''
        }
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setSelectedClinic(null);
          setConfirmAction(null);
        }}
        confirmText={confirmAction === 'activate' ? 'Activate' : 'Deactivate'}
        severity={confirmAction === 'deactivate' ? 'warning' : 'info'}
      />
    </Box>
  );
};

export default ClinicManagement;
